import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { startGoogleAuth } from '@/api/client';
import { useAuthStore } from '@/lib/auth-store';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Mark } from '@/components/AppShell';

/**
 * Sign in.
 *
 * Google only, and the role travels in a server-side state row rather than in
 * the URL - so this page cannot be edited into a different dashboard, and an
 * account registered as something else is refused rather than switched.
 *
 * The failure reasons come back on the redirect. Each gets its own sentence:
 * "login failed" on a role mismatch produces a support ticket nobody can
 * answer, because the person on the other end has no way to know the account
 * exists under a different role.
 */

const AUTH_FAILURE_COPY: Record<string, { title: string; body: string }> = {
  state_expired: {
    title: 'That took a little too long',
    body: 'The sign-in link timed out. Start again and it will work.',
  },
  state_reused: {
    title: 'That sign-in link was already used',
    body: 'For safety, each one works once. Start again.',
  },
  role_mismatch: {
    title: 'This account is not set up as a client',
    body: 'The Google account you chose belongs to a different kind of user here. Sign in with the account your account manager set up, or get in touch.',
  },
  not_provisioned: {
    title: 'We do not have an account for you yet',
    body: 'Your account manager needs to add you before you can sign in. Get in touch and they will sort it out.',
  },
  held: {
    title: 'Your account is paused',
    body: 'Nothing has been deleted. Get in touch and we will resume it.',
  },
  exchange_failed: {
    title: 'Google could not confirm that sign-in',
    body: 'Try again. If it keeps happening, try a different browser or get in touch.',
  },
  denied: {
    title: 'Sign-in was cancelled',
    body: 'You need to allow access to your Google account to sign in.',
  },
};

const UNKNOWN_FAILURE = {
  title: 'We could not sign you in',
  body: 'Try again. If it keeps happening, get in touch and we will look into it.',
};

export function LoginPage() {
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = useAuthStore((s) => s.accessToken);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);

  const failureReason = params.get('reason');
  const failed = params.get('auth') === 'failed';
  const failure = failed ? (AUTH_FAILURE_COPY[failureReason ?? ''] ?? UNKNOWN_FAILURE) : null;

  // Clear the failure out of the URL once it has been read, so a refresh does
  // not re-show a message about a sign-in attempt that is over.
  useEffect(() => {
    if (!failed) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      next.delete('auth');
      next.delete('reason');
      setParams(next, { replace: true });
    }, 15_000);
    return () => clearTimeout(timer);
  }, [failed, params, setParams]);

  if (bootstrapped && token) return <Navigate to="/" replace />;

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const { authorization_url } = await startGoogleAuth(`${window.location.origin}/auth/callback`);
      window.location.assign(authorization_url);
    } catch (err) {
      setBusy(false);
      setError(err instanceof ApiError ? err.message : 'We could not start sign-in. Try again in a moment.');
    }
  };

  return (
    // One card, centred. The two-panel sign-in this replaced spent half the
    // screen arguing the product's thesis at a person who had already decided
    // to sign in; the thesis now fits in the headline above the button.
    <div className="chassis flex flex-col items-center justify-center gap-[26px] overflow-y-auto px-[30px] pb-[30px] pt-10">
      <div className="w-full max-w-[26rem] rounded-xl bg-bone-raised px-8 pb-[30px] pt-[34px]">
        <div className="mb-[22px] flex items-center gap-2.5">
          <Mark className="h-[30px] w-[30px] text-graphite-900" />
          <span className="text-[1.375rem] font-bold leading-none tracking-[-0.035em] text-graphite-900">hourz</span>
        </div>

        <h1 className="text-[1.8125rem] font-bold leading-[1.14] tracking-[-0.03em] text-graphite-900">
          Ask for a change in plain English. Watch it happen, step by step.
        </h1>

        <Button variant="primary" size="lg" className="mt-[26px] h-14 w-full" loading={busy} onClick={() => void signIn()}>
          <GoogleGlyph />
          Continue with Google
        </Button>

        <p className="mt-3.5 text-sm text-graphite-300">
          Use the Google account your account manager set you up with.
        </p>

        {failure ? (
          <div className="mt-[22px] flex items-center gap-3.5 rounded-lg bg-alarm-wash px-5 py-[18px]" role="alert">
            <span
              aria-hidden
              className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-bone-raised/70 text-alarm"
            >
              <svg viewBox="0 0 12 12" className="h-[15px] w-[15px]" fill="none" stroke="currentColor" strokeWidth="1.6">
                <circle cx="6" cy="6" r="4.6" />
                <path d="M4.2 7.8 7.8 4.2" />
              </svg>
            </span>
            <p className="text-[0.9375rem] font-semibold text-alarm">
              {failure.title}
              <span className="mt-1 block font-normal text-graphite-500">{failure.body}</span>
            </p>
          </div>
        ) : null}

        {error ? <p className="mt-2.5 text-sm text-alarm">{error}</p> : null}

        <p className="mt-[22px] text-sm text-graphite-300">
          No account yet?{' '}
          <a
            href="mailto:support@example.com"
            className="text-graphite-500 underline decoration-rule-strong underline-offset-[3px] hover:text-signal"
          >
            Ask your account manager.
          </a>
        </p>
      </div>

      <p className="eyebrow max-w-[24rem] text-center">
        Every change is reviewed by a person before it goes live.
      </p>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 18 18" className="h-[17px] w-[17px] shrink-0">
      <path fill="currentColor" opacity=".9" d="M17.6 9.2c0-.6-.1-1.2-.2-1.8H9v3.5h4.8a4.1 4.1 0 0 1-1.8 2.7v2.2h2.9c1.7-1.6 2.7-3.9 2.7-6.6z" />
      <path fill="currentColor" opacity=".72" d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.2c-.8.5-1.8.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8H.9v2.3A9 9 0 0 0 9 18z" />
      <path fill="currentColor" opacity=".55" d="M3.9 10.7a5.4 5.4 0 0 1 0-3.4V5H.9a9 9 0 0 0 0 8l3-2.3z" />
      <path fill="currentColor" opacity=".82" d="M9 3.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6A9 9 0 0 0 .9 5l3 2.3C4.6 5.2 6.6 3.6 9 3.6z" />
    </svg>
  );
}
